import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Group,
  Line,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Plane,
  ShapeUtils,
  Vector2,
  Vector3,
  type Object3D,
} from 'three'
import { areLinesConnected } from './line'

const SLICE_HEIGHT_M = 0.01

/**
 * Represents a 2D contour at a specific Z height
 */
export interface SliceContour {
  z: number
  points: Vector3[]
  isClosed: boolean
}

/**
 * Get the world bounding box of a mesh, taking into account its position in the hierarchy
 */
export function getWorldBoundingBox(object: Object3D): Box3 {
  const bbox = new Box3()

  object.traverse((child) => {
    if (child instanceof Mesh) {
      const childBbox = new Box3().setFromObject(child)
      bbox.union(childBbox)
    }
  })

  return bbox
}

/**
 * Calculate the Z levels where slices should be made
 * Starts from the bottom of the object and goes up every SLICE_HEIGHT_M
 */
export function calculateSliceLevels(object: Object3D, sliceHeight: number = SLICE_HEIGHT_M): number[] {
  const bbox = getWorldBoundingBox(object)
  const levels: number[] = []

  // Start slightly above the bottom to avoid edge cases
  const startZ = bbox.min.z + 0.001
  const endZ = bbox.max.z - 0.001

  for (let z = startZ; z <= endZ; z += sliceHeight) {
    levels.push(z)
  }

  return levels
}

/**
 * Find intersections between a plane and a triangle
 * Returns the two intersection points if the plane intersects the triangle
 */
function getPlaneTriangleIntersection(
  plane: Plane,
  p1: Vector3,
  p2: Vector3,
  p3: Vector3,
): Vector3[] | null {
  const points = [p1, p2, p3]
  const distances = points.map(p => plane.distanceToPoint(p))

  const intersections: Vector3[] = []

  // Check each edge of the triangle
  const edges = [
    [0, 1],
    [1, 2],
    [2, 0],
  ]

  for (const [i, j] of edges) {
    const d1 = distances[i]
    const d2 = distances[j]

    // Edge crosses the plane if signs are different
    if (d1 * d2 < 0) {
      const t = d1 / (d1 - d2)
      const intersection = new Vector3().lerpVectors(points[i], points[j], t)
      intersections.push(intersection)
    }
    // Point is exactly on the plane
    else if (Math.abs(d1) < 1e-10) {
      intersections.push(points[i].clone())
    }
  }

  // Remove duplicates (for vertices exactly on the plane)
  const uniqueIntersections: Vector3[] = []
  for (const p of intersections) {
    const isDuplicate = uniqueIntersections.some(
      existing => existing.distanceTo(p) < 1e-6,
    )
    if (!isDuplicate) {
      uniqueIntersections.push(p)
    }
  }

  return uniqueIntersections.length >= 2 ? uniqueIntersections.slice(0, 2) : null
}

/**
 * Helper function to iterate over all triangles in a mesh geometry
 * Calls the callback for each triangle with its three vertex indices
 */
function forEachTriangle(
  mesh: Mesh,
  callback: (i1: number, i2: number, i3: number, triangleIndex: number) => void,
): void {
  const geometry = mesh.geometry
  const indexAttribute = geometry.index

  const triangleCount = indexAttribute
    ? indexAttribute.count / 3
    : geometry.attributes.position.count / 3

  for (let i = 0; i < triangleCount; i++) {
    let i1: number, i2: number, i3: number

    if (indexAttribute) {
      i1 = indexAttribute.getX(i * 3)
      i2 = indexAttribute.getX(i * 3 + 1)
      i3 = indexAttribute.getX(i * 3 + 2)
    }
    else {
      i1 = i * 3
      i2 = i * 3 + 1
      i3 = i * 3 + 2
    }

    callback(i1, i2, i3, i)
  }
}

/**
 * Helper function to get a world-space vertex from a mesh at a given index
 */
function getWorldVertex(
  mesh: Mesh,
  index: number,
  worldMatrix: Matrix4,
): Vector3 {
  const positionAttribute = mesh.geometry.attributes.position
  const vertex = new Vector3(
    positionAttribute.getX(index),
    positionAttribute.getY(index),
    positionAttribute.getZ(index),
  )
  return vertex.applyMatrix4(worldMatrix)
}

/**
 * Slice a mesh at a given Z height and return the intersection segments
 */
export function sliceMeshAtZ(mesh: Mesh, z: number): { start: Vector3, end: Vector3 }[] {
  const segments: { start: Vector3, end: Vector3 }[] = []

  // Create a horizontal plane at height z (normal pointing up)
  const plane = new Plane(new Vector3(0, 0, 1), -z)

  // Get world matrix to transform local vertices to world coordinates
  mesh.updateMatrixWorld(true)
  const worldMatrix = mesh.matrixWorld

  // Process each triangle
  forEachTriangle(mesh, (i1, i2, i3) => {
    const v1 = getWorldVertex(mesh, i1, worldMatrix)
    const v2 = getWorldVertex(mesh, i2, worldMatrix)
    const v3 = getWorldVertex(mesh, i3, worldMatrix)

    const intersections = getPlaneTriangleIntersection(plane, v1, v2, v3)
    if (intersections && intersections.length === 2) {
      segments.push({ start: intersections[0], end: intersections[1] })
    }
  })

  return segments
}

/**
 * Build contours from line segments at a given Z level
 * This connects the segments to form closed or open contours
 */
export function buildContoursFromSegments(
  segments: { start: Vector3, end: Vector3 }[],
  tolerance: number = 1e-4,
): SliceContour[] {
  if (segments.length === 0) return []

  const contours: SliceContour[] = []
  const used = new Set<number>()

  const findConnectedSegment = (point: Vector3, excludeIndex: number): number => {
    for (let i = 0; i < segments.length; i++) {
      if (used.has(i) || i === excludeIndex) continue

      if (segments[i].start.distanceTo(point) < tolerance) {
        return i
      }
      if (segments[i].end.distanceTo(point) < tolerance) {
        return i
      }
    }
    return -1
  }

  for (let startIdx = 0; startIdx < segments.length; startIdx++) {
    if (used.has(startIdx)) continue

    const contourPoints: Vector3[] = [segments[startIdx].start.clone(), segments[startIdx].end.clone()]
    used.add(startIdx)

    // Try to extend the contour in both directions
    let changed = true
    while (changed) {
      changed = false

      // Try to extend from the end
      const endPoint = contourPoints[contourPoints.length - 1]
      const nextIdx = findConnectedSegment(endPoint, -1)
      if (nextIdx !== -1) {
        used.add(nextIdx)
        const seg = segments[nextIdx]
        if (seg.start.distanceTo(endPoint) < tolerance) {
          contourPoints.push(seg.end.clone())
        }
        else {
          contourPoints.push(seg.start.clone())
        }
        changed = true
      }

      // Try to extend from the start
      const startPoint = contourPoints[0]
      const prevIdx = findConnectedSegment(startPoint, -1)
      if (prevIdx !== -1) {
        used.add(prevIdx)
        const seg = segments[prevIdx]
        if (seg.end.distanceTo(startPoint) < tolerance) {
          contourPoints.unshift(seg.start.clone())
        }
        else {
          contourPoints.unshift(seg.end.clone())
        }
        changed = true
      }
    }

    // Check if contour is closed
    const isClosed = contourPoints[0].distanceTo(contourPoints[contourPoints.length - 1]) < tolerance

    contours.push({
      z: contourPoints[0].z,
      points: contourPoints,
      isClosed,
    })
  }

  return contours
}

/**
 * Get all contours for all slice levels
 */
export function getAllSliceContours(
  object: Object3D,
  sliceHeight: number = SLICE_HEIGHT_M,
): Map<number, SliceContour[]> {
  const levels = calculateSliceLevels(object, sliceHeight)
  const allContours = new Map<number, SliceContour[]>()

  object.traverse((child) => {
    if (child instanceof Mesh) {
      for (const z of levels) {
        const segments = sliceMeshAtZ(child, z)
        const contours = buildContoursFromSegments(segments)

        const existing = allContours.get(z) || []
        allContours.set(z, [...existing, ...contours])
      }
    }
  })

  return allContours
}

/**
 * Calculate a simple signature for a contour based on its bounding box and point count
 * Used for quick comparison between contours
 */
function getContourSignature(contour: SliceContour): {
  minX: number
  maxX: number
  minY: number
  maxY: number
  pointCount: number
  area: number
} {
  let minX = Infinity, maxX = -Infinity
  let minY = Infinity, maxY = -Infinity

  for (const p of contour.points) {
    minX = Math.min(minX, p.x)
    maxX = Math.max(maxX, p.x)
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
  }

  // Approximate area using shoelace formula for 2D polygon
  let area = 0
  const n = contour.points.length
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    area += contour.points[i].x * contour.points[j].y
    area -= contour.points[j].x * contour.points[i].y
  }
  area = Math.abs(area) / 2

  return {
    minX,
    maxX,
    minY,
    maxY,
    pointCount: contour.points.length,
    area,
  }
}

/**
 * Represents a layer analysis result
 */
export interface LayerAnalysis {
  z: number
  contourCount: number
  validContourCount: number // Contours with significant area
  totalArea: number
  signatures: ReturnType<typeof getContourSignature>[]
}

// Minimum area threshold to consider a contour valid (filters out degenerate contours)
const MIN_CONTOUR_AREA = 0.001 // 0.001 m² = 10 cm²

/**
 * Represents a detected change between layers
 */
export interface LayerChange {
  lowerZ: number // Z of the layer below the change
  upperZ: number // Z of the layer above the change
  lowerContourCount: number
  upperContourCount: number
  isMerge: boolean // true if shapes merge going up (count decreases)
}

/**
 * Analyze contours at each layer and detect changes
 * Filters out degenerate contours (area ≈ 0) for more accurate analysis
 */
export function analyzeContourChanges(
  contours: Map<number, SliceContour[]>,
): {
  layers: LayerAnalysis[]
  changePoints: number[] // Z values where significant changes occur (legacy)
  changes: LayerChange[] // Detailed change information
} {
  const layers: LayerAnalysis[] = []
  const changePoints: number[] = []
  const changes: LayerChange[] = []

  // Sort levels by Z
  const sortedLevels = Array.from(contours.keys()).sort((a, b) => a - b)

  for (const z of sortedLevels) {
    const layerContours = contours.get(z) || []
    const signatures = layerContours.map(c => getContourSignature(c))

    // Filter out degenerate contours (very small area)
    const validSignatures = signatures.filter(s => s.area > MIN_CONTOUR_AREA)
    const totalArea = validSignatures.reduce((sum, s) => sum + s.area, 0)

    layers.push({
      z,
      contourCount: layerContours.length,
      validContourCount: validSignatures.length,
      totalArea,
      signatures: validSignatures,
    })
  }

  // Detect change points (where VALID contour count or area changes significantly)
  const AREA_CHANGE_THRESHOLD = 0.1 // 10% change threshold

  for (let i = 1; i < layers.length; i++) {
    const prev = layers[i - 1]
    const curr = layers[i]

    // Check for valid contour count change (ignore degenerate contours)
    const countChanged = prev.validContourCount !== curr.validContourCount

    // Check for significant area change
    const areaRatio = prev.totalArea > 0 ? Math.abs(curr.totalArea - prev.totalArea) / prev.totalArea : 0
    const areaChanged = areaRatio > AREA_CHANGE_THRESHOLD

    if (countChanged || areaChanged) {
      changePoints.push(curr.z)
      changes.push({
        lowerZ: prev.z,
        upperZ: curr.z,
        lowerContourCount: prev.validContourCount,
        upperContourCount: curr.validContourCount,
        isMerge: curr.validContourCount < prev.validContourCount,
      })
    }
  }

  return { layers, changePoints, changes }
}

/**
 * Represents a Z-range zone for splitting
 */
interface SplitZone {
  minZ: number
  maxZ: number
  contourCount: number
  contours: SliceContour[] // The actual contours for this zone (for XY separation)
}

/**
 * Find the actual merge/split point between two layers by examining mesh geometry.
 * When shapes merge (going up), finds the lowest Z point where triangles bridge
 * between different contour regions.
 * When shapes split (going up), finds the highest Z point where triangles bridge.
 *
 * @param object The object containing meshes to examine
 * @param change The layer change information
 * @param lowerContours The contours at the lower layer (used to determine XY regions)
 * @returns The precise Z coordinate for the split, or null if not found
 */
function findPreciseMergePoint(
  object: Object3D,
  change: LayerChange,
  lowerContours: SliceContour[],
): number | null {
  const { lowerZ, upperZ, isMerge } = change

  // Filter to valid contours only
  const validContours = lowerContours.filter((c) => {
    const sig = getContourSignature(c)
    return sig.area > MIN_CONTOUR_AREA
  })

  // If we don't have multiple contours at the lower level, we can't detect bridging
  if (validContours.length < 2) {
    return null
  }

  let bridgingZ: number | null = null

  object.traverse((child) => {
    if (!(child instanceof Mesh)) return

    const mesh = child as Mesh
    mesh.updateMatrixWorld(true)
    const worldMatrix = mesh.matrixWorld

    forEachTriangle(mesh, (i1, i2, i3) => {
      const v1 = getWorldVertex(mesh, i1, worldMatrix)
      const v2 = getWorldVertex(mesh, i2, worldMatrix)
      const v3 = getWorldVertex(mesh, i3, worldMatrix)

      const vertices = [v1, v2, v3]

      // Check if any vertex is in the region between layers
      const verticesInRange = vertices.filter(v => v.z > lowerZ && v.z < upperZ)
      if (verticesInRange.length === 0) return

      // Determine which contour each vertex belongs to (by XY position)
      const contourIndices = new Set<number>()
      for (const v of vertices) {
        const idx = findContourForPoint(v.x, v.y, validContours)
        contourIndices.add(idx)
      }

      // If vertices belong to different contours, this triangle bridges between regions
      if (contourIndices.size > 1) {
        // For merges (shapes combining going up): find the lowest Z of bridging vertices
        // For splits (shapes separating going up): find the highest Z of bridging vertices
        for (const v of verticesInRange) {
          if (isMerge) {
            // When merging, we want the lowest bridging point
            if (bridgingZ === null || v.z < bridgingZ) {
              bridgingZ = v.z
            }
          }
          else {
            // When splitting, we want the highest bridging point below the split
            if (bridgingZ === null || v.z > bridgingZ) {
              bridgingZ = v.z
            }
          }
        }
      }
    })
  })

  return bridgingZ
}

/**
 * Determine the split zones based on change points
 * Uses precise merge point detection to split just below actual merge points
 */
export function determineSplitZones(
  object: Object3D,
  sliceHeight: number = SLICE_HEIGHT_M,
): SplitZone[] {
  const contours = getAllSliceContours(object, sliceHeight)
  const { layers, changes } = analyzeContourChanges(contours)
  const bbox = getWorldBoundingBox(object)

  const zones: SplitZone[] = []

  // Small epsilon to split just below the merge point
  const SPLIT_EPSILON = 0.0001

  // Calculate precise split points for each change
  const preciseSplitPoints: number[] = []

  for (const change of changes) {
    // Get contours at the lower layer for XY region detection
    const lowerContours = contours.get(change.lowerZ) || []

    // Try to find the precise merge/split point
    const preciseZ = findPreciseMergePoint(object, change, lowerContours)

    if (preciseZ !== null) {
      if (change.isMerge) {
        // For merges: split just below the merge point so bridging vertices go to upper shape
        preciseSplitPoints.push(preciseZ - SPLIT_EPSILON)
      }
      else {
        // For splits: split just above the split point
        preciseSplitPoints.push(preciseZ + SPLIT_EPSILON)
      }
    }
    else {
      // Fallback to the detected layer Z if precise point not found
      preciseSplitPoints.push(change.upperZ)
    }
  }

  // Sort split points
  const sortedSplitPoints = [...preciseSplitPoints].sort((a, b) => a - b)

  // Add boundaries
  const boundaries = [bbox.min.z, ...sortedSplitPoints, bbox.max.z]

  for (let i = 0; i < boundaries.length - 1; i++) {
    const minZ = boundaries[i]
    const maxZ = boundaries[i + 1]

    // Find a layer in this range to get contour info
    const midZ = (minZ + maxZ) / 2
    const layerInRange = layers.find(l => l.z >= minZ && l.z < maxZ) || layers.find(l => Math.abs(l.z - midZ) < sliceHeight)
    const contourCount = layerInRange?.validContourCount || 1

    // Get the actual contours for this layer (filtered by area)
    const layerZ = layerInRange?.z || midZ
    const layerContours = contours.get(layerZ) || []
    const validContours = layerContours.filter((c) => {
      const sig = getContourSignature(c)
      return sig.area > MIN_CONTOUR_AREA
    })

    zones.push({ minZ, maxZ, contourCount, contours: validContours })
  }

  return zones
}

/**
 * Core ray-casting point-in-polygon test on a raw Vector3[] polygon.
 */
function isPointInPolygon(x: number, y: number, points: Vector3[]): boolean {
  let inside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x, yi = points[i].y
    const xj = points[j].x, yj = points[j].y
    const intersect = ((yi > y) !== (yj > y))
      && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

/**
 * Point-in-polygon test using ray casting algorithm
 * Tests if a 2D point (x, y) is inside a polygon defined by contour points
 */
export function isPointInContour(x: number, y: number, contour: SliceContour): boolean {
  return isPointInPolygon(x, y, contour.points)
}

/**
 * Calculate minimum distance from a point to a contour (2D)
 */
export function distanceToContour(x: number, y: number, contour: SliceContour): number {
  let minDist = Infinity
  const points = contour.points

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length
    const p1 = points[i]
    const p2 = points[j]

    // Distance to line segment
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const len2 = dx * dx + dy * dy

    let dist: number
    if (len2 === 0) {
      // Degenerate segment (point)
      dist = Math.sqrt((x - p1.x) ** 2 + (y - p1.y) ** 2)
    }
    else {
      // Project point onto line segment
      let t = ((x - p1.x) * dx + (y - p1.y) * dy) / len2
      t = Math.max(0, Math.min(1, t))
      const projX = p1.x + t * dx
      const projY = p1.y + t * dy
      dist = Math.sqrt((x - projX) ** 2 + (y - projY) ** 2)
    }

    minDist = Math.min(minDist, dist)
  }

  return minDist
}

/**
 * Find which contour a point belongs to
 * First tries point-in-polygon, then falls back to nearest contour
 */
function findContourForPoint(x: number, y: number, contours: SliceContour[]): number {
  // First, try exact point-in-polygon test
  for (let i = 0; i < contours.length; i++) {
    if (isPointInContour(x, y, contours[i])) {
      return i
    }
  }

  // Point not inside any contour - find the nearest one
  // This handles points on edges/walls
  let nearestIdx = 0
  let minDist = Infinity

  for (let i = 0; i < contours.length; i++) {
    const dist = distanceToContour(x, y, contours[i])
    if (dist < minDist) {
      minDist = dist
      nearestIdx = i
    }
  }

  return nearestIdx
}

/**
 * Separate triangles by which contour region they belong to
 * Returns an array of triangle arrays, one per contour
 */
function separateTrianglesByContour(
  triangles: Vector3[][],
  contours: SliceContour[],
): Map<number, Vector3[][]> {
  const result = new Map<number, Vector3[][]>()

  // Initialize with empty arrays for each contour
  for (let i = 0; i < contours.length; i++) {
    result.set(i, [])
  }

  for (const tri of triangles) {
    // Use centroid XY to determine which contour this triangle belongs to
    const centroidX = (tri[0].x + tri[1].x + tri[2].x) / 3
    const centroidY = (tri[0].y + tri[1].y + tri[2].y) / 3

    const contourIndex = findContourForPoint(centroidX, centroidY, contours)
    result.get(contourIndex)!.push(tri)
  }

  return result
}

/**
 * Clip a single triangle against a Z plane, keeping the portion BELOW (z <= cutZ)
 * Preserves winding order by processing vertices in order
 *
 * Uses Sutherland-Hodgman polygon clipping adapted for triangles
 */
export function clipTriangleBelowZ(
  v1: Vector3,
  v2: Vector3,
  v3: Vector3,
  cutZ: number,
): Vector3[][] {
  // Process edges in order to preserve winding
  const inputVertices = [v1, v2, v3]
  const outputVertices: Vector3[] = []

  for (let i = 0; i < 3; i++) {
    const current = inputVertices[i]
    const next = inputVertices[(i + 1) % 3]

    const currentBelow = current.z <= cutZ
    const nextBelow = next.z <= cutZ

    if (currentBelow) {
      // Current vertex is inside (below plane)
      outputVertices.push(current.clone())

      if (!nextBelow) {
        // Next is outside, add intersection
        const t = (cutZ - current.z) / (next.z - current.z)
        const intersection = new Vector3().lerpVectors(current, next, t)
        intersection.z = cutZ
        outputVertices.push(intersection)
      }
    }
    else if (nextBelow) {
      // Current is outside, next is inside - add intersection
      const t = (cutZ - current.z) / (next.z - current.z)
      const intersection = new Vector3().lerpVectors(current, next, t)
      intersection.z = cutZ
      outputVertices.push(intersection)
    }
    // If both outside, add nothing
  }

  // Convert polygon to triangles (fan triangulation)
  if (outputVertices.length < 3) {
    return []
  }

  const triangles: Vector3[][] = []
  for (let i = 1; i < outputVertices.length - 1; i++) {
    triangles.push([
      outputVertices[0].clone(),
      outputVertices[i].clone(),
      outputVertices[i + 1].clone(),
    ])
  }

  return triangles
}

/**
 * Clip a single triangle against a Z plane, keeping the portion ABOVE (z >= cutZ)
 * Preserves winding order by processing vertices in order
 */
export function clipTriangleAboveZ(
  v1: Vector3,
  v2: Vector3,
  v3: Vector3,
  cutZ: number,
): Vector3[][] {
  // Process edges in order to preserve winding
  const inputVertices = [v1, v2, v3]
  const outputVertices: Vector3[] = []

  for (let i = 0; i < 3; i++) {
    const current = inputVertices[i]
    const next = inputVertices[(i + 1) % 3]

    const currentAbove = current.z >= cutZ
    const nextAbove = next.z >= cutZ

    if (currentAbove) {
      // Current vertex is inside (above plane)
      outputVertices.push(current.clone())

      if (!nextAbove) {
        // Next is outside, add intersection
        const t = (cutZ - current.z) / (next.z - current.z)
        const intersection = new Vector3().lerpVectors(current, next, t)
        intersection.z = cutZ
        outputVertices.push(intersection)
      }
    }
    else if (nextAbove) {
      // Current is outside, next is inside - add intersection
      const t = (cutZ - current.z) / (next.z - current.z)
      const intersection = new Vector3().lerpVectors(current, next, t)
      intersection.z = cutZ
      outputVertices.push(intersection)
    }
  }

  // Convert polygon to triangles (fan triangulation)
  if (outputVertices.length < 3) {
    return []
  }

  const triangles: Vector3[][] = []
  for (let i = 1; i < outputVertices.length - 1; i++) {
    triangles.push([
      outputVertices[0].clone(),
      outputVertices[i].clone(),
      outputVertices[i + 1].clone(),
    ])
  }

  return triangles
}

/**
 * Clip a list of triangles to keep only portions within [minZ, maxZ]
 * This performs actual geometric clipping, not just filtering
 */
export function clipTrianglesToZRange(
  triangles: Vector3[][],
  minZ: number,
  maxZ: number,
): Vector3[][] {
  let result = triangles

  // First clip by minZ (keep above)
  let clipped: Vector3[][] = []
  for (const tri of result) {
    const clippedTris = clipTriangleAboveZ(tri[0], tri[1], tri[2], minZ)
    clipped.push(...clippedTris)
  }
  result = clipped

  // Then clip by maxZ (keep below)
  clipped = []
  for (const tri of result) {
    const clippedTris = clipTriangleBelowZ(tri[0], tri[1], tri[2], maxZ)
    clipped.push(...clippedTris)
  }

  return clipped
}

/**
 * Collect cut edges from triangles at a specific Z plane
 * Edges are deduplicated - interior edges (shared by 2 triangles) are removed,
 * only boundary edges remain.
 */
function collectBoundaryEdgesAtZ(
  triangles: Vector3[][],
  z: number,
  tolerance: number = 1e-4,
): { start: Vector3, end: Vector3 }[] {
  // Use a map to count edge occurrences
  // Key: serialized edge (sorted by coordinates to handle direction)
  const edgeMap = new Map<string, { start: Vector3, end: Vector3, count: number }>()

  const makeEdgeKey = (v1: Vector3, v2: Vector3): string => {
    // Create a canonical key regardless of edge direction
    const p1 = `${v1.x.toFixed(6)},${v1.y.toFixed(6)}`
    const p2 = `${v2.x.toFixed(6)},${v2.y.toFixed(6)}`
    return p1 < p2 ? `${p1}|${p2}` : `${p2}|${p1}`
  }

  for (const tri of triangles) {
    // Find edges that lie on the cut plane
    for (let i = 0; i < 3; i++) {
      const v1 = tri[i]
      const v2 = tri[(i + 1) % 3]

      // Check if both vertices are on the cut plane
      if (Math.abs(v1.z - z) < tolerance && Math.abs(v2.z - z) < tolerance) {
        // Avoid degenerate edges
        if (v1.distanceTo(v2) > tolerance) {
          const key = makeEdgeKey(v1, v2)
          const existing = edgeMap.get(key)
          if (existing) {
            existing.count++
          }
          else {
            edgeMap.set(key, { start: v1.clone(), end: v2.clone(), count: 1 })
          }
        }
      }
    }
  }

  // Keep only boundary edges (count == 1)
  // Interior edges are shared by 2 triangles and should be removed
  const boundaryEdges: { start: Vector3, end: Vector3 }[] = []
  for (const edge of edgeMap.values()) {
    if (edge.count === 1) {
      boundaryEdges.push({ start: edge.start, end: edge.end })
    }
  }

  return boundaryEdges
}

/**
 * Build closed loops from edges
 * Returns arrays of points forming closed loops
 */
function buildClosedLoopsFromEdges(
  edges: { start: Vector3, end: Vector3 }[],
  tolerance: number = 1e-3,
): Vector3[][] {
  if (edges.length === 0) return []

  const loops: Vector3[][] = []
  const used = new Set<number>()

  const findConnectedEdge = (point: Vector3): { index: number, reversed: boolean } | null => {
    let bestMatch: { index: number, reversed: boolean, dist: number } | null = null

    for (let i = 0; i < edges.length; i++) {
      if (used.has(i)) continue

      const distStart = edges[i].start.distanceTo(point)
      const distEnd = edges[i].end.distanceTo(point)

      if (distStart < tolerance && (!bestMatch || distStart < bestMatch.dist)) {
        bestMatch = { index: i, reversed: false, dist: distStart }
      }
      if (distEnd < tolerance && (!bestMatch || distEnd < bestMatch.dist)) {
        bestMatch = { index: i, reversed: true, dist: distEnd }
      }
    }

    return bestMatch ? { index: bestMatch.index, reversed: bestMatch.reversed } : null
  }

  for (let startIdx = 0; startIdx < edges.length; startIdx++) {
    if (used.has(startIdx)) continue

    const loopPoints: Vector3[] = [edges[startIdx].start.clone(), edges[startIdx].end.clone()]
    used.add(startIdx)

    // Try to close the loop by extending from the end
    let maxIterations = edges.length
    while (maxIterations-- > 0) {
      const endPoint = loopPoints[loopPoints.length - 1]
      const startPoint = loopPoints[0]

      // Check if loop is closed
      if (loopPoints.length > 2 && endPoint.distanceTo(startPoint) < tolerance) {
        break
      }

      const next = findConnectedEdge(endPoint)
      if (!next) break

      used.add(next.index)
      const edge = edges[next.index]
      if (next.reversed) {
        loopPoints.push(edge.start.clone())
      }
      else {
        loopPoints.push(edge.end.clone())
      }
    }

    // Only keep closed loops with at least 3 points
    if (loopPoints.length >= 3) {
      const firstPoint = loopPoints[0]
      const lastPoint = loopPoints[loopPoints.length - 1]
      if (firstPoint.distanceTo(lastPoint) < tolerance) {
        loops.push(loopPoints)
      }
    }
  }

  return loops
}

/**
 * Calculate the signed area of a polygon (2D, using X and Y)
 * Positive = counter-clockwise, Negative = clockwise
 */
export function signedPolygonArea(points: Vector3[]): number {
  let sum = 0
  const n = points.length
  for (let i = 0; i < n; i++) {
    const p1 = points[i]
    const p2 = points[(i + 1) % n]
    sum += (p2.x - p1.x) * (p2.y + p1.y)
  }
  return -sum / 2 // Negative because of coordinate system
}

/**
 * Check if a polygon is counter-clockwise when viewed from +Z
 */
function isCounterClockwise(points: Vector3[]): boolean {
  return signedPolygonArea(points) > 0
}

/**
 * Calculate the cross product of vectors (p1->p2) and (p1->p3) in 2D
 * Positive = left turn (CCW), Negative = right turn (CW), Zero = collinear
 */
export function cross2D(p1: Vector3, p2: Vector3, p3: Vector3): number {
  return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x)
}

/**
 * Check if point P is inside triangle ABC (2D)
 */
export function isPointInTriangle2D(p: Vector3, a: Vector3, b: Vector3, c: Vector3): boolean {
  const d1 = cross2D(p, a, b)
  const d2 = cross2D(p, b, c)
  const d3 = cross2D(p, c, a)

  const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0)
  const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0)

  return !(hasNeg && hasPos)
}

/**
 * Check if vertex at index i is an "ear" in the polygon
 * An ear is a triangle that:
 * 1. Has a convex vertex (for CCW polygon, left turn)
 * 2. Contains no other polygon vertices inside it
 */
function isEar(
  vertices: Vector3[],
  i: number,
  isCCW: boolean,
): boolean {
  const n = vertices.length
  const prev = (i - 1 + n) % n
  const next = (i + 1) % n

  const a = vertices[prev]
  const b = vertices[i]
  const c = vertices[next]

  // Check if vertex b is convex
  const cross = cross2D(a, b, c)

  // For CCW polygon, convex vertex has positive cross product
  // For CW polygon, convex vertex has negative cross product
  if (isCCW && cross <= 0) return false
  if (!isCCW && cross >= 0) return false

  // Check that no other vertex is inside the triangle
  for (let j = 0; j < n; j++) {
    if (j === prev || j === i || j === next) continue

    if (isPointInTriangle2D(vertices[j], a, b, c)) {
      return false
    }
  }

  return true
}

/**
 * Triangulate a polygon using the Ear Clipping algorithm
 * Works for both convex and concave simple polygons
 *
 * For a cap at minZ (bottom), triangles should face down (-Z)
 * For a cap at maxZ (top), triangles should face up (+Z)
 */
export function triangulatePolygonEarClipping(
  points: Vector3[],
  faceUp: boolean,
): Vector3[][] {
  if (points.length < 3) return []
  if (points.length === 3) {
    // Already a triangle
    const isCCW = isCounterClockwise(points)
    const needReverse = faceUp ? !isCCW : isCCW
    if (needReverse) {
      return [[points[2].clone(), points[1].clone(), points[0].clone()]]
    }
    return [[points[0].clone(), points[1].clone(), points[2].clone()]]
  }

  const triangles: Vector3[][] = []

  // Make a copy of the vertices to work with
  const vertices = points.map(p => p.clone())

  // Determine winding order
  const isCCW = isCounterClockwise(vertices)

  // For the output triangles:
  // - faceUp (top cap) needs CCW winding when viewed from +Z
  // - faceDown (bottom cap) needs CW winding when viewed from +Z
  const outputCCW = faceUp

  // Process ears until only 3 vertices remain
  let iterations = 0
  const maxIterations = vertices.length * vertices.length // Safety limit

  while (vertices.length > 3 && iterations < maxIterations) {
    iterations++
    let earFound = false

    for (let i = 0; i < vertices.length; i++) {
      if (isEar(vertices, i, isCCW)) {
        const n = vertices.length
        const prev = (i - 1 + n) % n
        const next = (i + 1) % n

        // Add the ear triangle
        const a = vertices[prev]
        const b = vertices[i]
        const c = vertices[next]

        // Adjust winding for output
        if (isCCW === outputCCW) {
          triangles.push([a.clone(), b.clone(), c.clone()])
        }
        else {
          triangles.push([c.clone(), b.clone(), a.clone()])
        }

        // Remove the ear vertex
        vertices.splice(i, 1)
        earFound = true
        break
      }
    }

    if (!earFound) {
      // No ear found - polygon might be degenerate
      // Fall back to fan triangulation for remaining vertices
      break
    }
  }

  // Add the last triangle
  if (vertices.length === 3) {
    if (isCCW === outputCCW) {
      triangles.push([vertices[0].clone(), vertices[1].clone(), vertices[2].clone()])
    }
    else {
      triangles.push([vertices[2].clone(), vertices[1].clone(), vertices[0].clone()])
    }
  }

  return triangles
}

/**
 * Test if a 2D point (x, y) is inside a polygon defined by Vector3[] points.
 * Delegates to the shared isPointInPolygon helper.
 */
function isPointInLoop(x: number, y: number, loop: Vector3[]): boolean {
  return isPointInPolygon(x, y, loop)
}

/**
 * Normalize a closed loop by removing the duplicate closing point
 * (buildClosedLoopsFromEdges returns loops where last point ≈ first point)
 */
function normalizeLoop(loop: Vector3[], tolerance: number = 1e-3): Vector3[] {
  if (loop.length < 2) return loop
  const first = loop[0]
  const last = loop.at(-1)!
  if (first.distanceTo(last) < tolerance) {
    return loop.slice(0, -1)
  }
  return loop
}

/**
 * Check if loopA is contained inside loopB by testing if a point of loopA is inside loopB
 */
function isLoopInsideLoop(inner: Vector3[], outer: Vector3[]): boolean {
  return isPointInLoop(inner[0].x, inner[0].y, outer)
}

/**
 * Triangulate an outer contour with holes using ShapeUtils.triangulateShape.
 * Returns triangles with correct winding order for the given face direction.
 */
function triangulateContourWithHoles(
  loop: Vector3[],
  holes: Vector3[][],
  z: number,
  faceUp: boolean,
): Vector3[][] {
  const contour2D = loop.map(p => new Vector2(p.x, p.y))
  const holes2D = holes.map(h => h.map(p => new Vector2(p.x, p.y)))

  const faces = ShapeUtils.triangulateShape(contour2D, holes2D)

  const allPoints2D = [...contour2D]
  for (const h of holes2D) {
    allPoints2D.push(...h)
  }

  const result: Vector3[][] = []
  for (const face of faces) {
    const [a, b, c] = face
    const p1 = new Vector3(allPoints2D[a].x, allPoints2D[a].y, z)
    const p2 = new Vector3(allPoints2D[b].x, allPoints2D[b].y, z)
    const p3 = new Vector3(allPoints2D[c].x, allPoints2D[c].y, z)

    const triArea = signedPolygonArea([p1, p2, p3])
    const needsReverse = (faceUp && triArea < 0) || (!faceUp && triArea > 0)
    result.push(needsReverse ? [p3, p2, p1] : [p1, p2, p3])
  }

  return result
}

/**
 * Generate cap triangles for cut surfaces at a specific Z level.
 * Handles nested loops (holes) by using ShapeUtils.triangulateShape,
 * so hollow shapes (rings, tubes) are preserved.
 */
export function generateCapTriangles(
  triangles: Vector3[][],
  z: number,
  faceUp: boolean,
): Vector3[][] {
  // Collect boundary edges on the cut plane (deduplicated)
  const boundaryEdges = collectBoundaryEdgesAtZ(triangles, z)

  if (boundaryEdges.length === 0) return []

  // Build closed loops from boundary edges and normalize (remove duplicate closing point)
  const rawLoops = buildClosedLoopsFromEdges(boundaryEdges)
  const loops = rawLoops.map(l => normalizeLoop(l))

  if (loops.length === 0) return []

  // If only one loop, use simple ear clipping (no holes)
  if (loops.length === 1) {
    return triangulatePolygonEarClipping(loops[0], faceUp)
  }

  // Multiple loops: classify using nesting depth (parity-based).
  // Compute each loop's area and sort by area descending.
  const loopData = loops.map((loop, index) => ({
    index,
    loop,
    area: Math.abs(signedPolygonArea(loop)),
  }))
  loopData.sort((a, b) => b.area - a.area)

  // For each loop, find its immediate parent (smallest containing loop)
  // then compute depth. Even depth = outer contour, odd depth = hole.
  const parentIndex: (number | null)[] = new Array(loopData.length).fill(null)
  const depth: number[] = new Array(loopData.length).fill(0)

  for (let i = 0; i < loopData.length; i++) {
    // Search for the smallest loop (highest index in sorted array) that contains this one
    for (let j = i - 1; j >= 0; j--) {
      if (isLoopInsideLoop(loopData[i].loop, loopData[j].loop)) {
        parentIndex[i] = j
        depth[i] = depth[j] + 1
        break
      }
    }
  }

  // Group: outer contours (even depth) collect their direct hole children (odd depth).
  // Additionally verify winding: a hole must have opposite winding to its parent outer contour.
  const outerLoops: { loop: Vector3[], holes: Vector3[][] }[] = []
  const outerIndexMap = new Map<number, number>() // loopData index → outerLoops index

  for (let i = 0; i < loopData.length; i++) {
    const winding = signedPolygonArea(loopData[i].loop)

    if (depth[i] % 2 === 0) {
      // Outer contour
      outerIndexMap.set(i, outerLoops.length)
      outerLoops.push({ loop: loopData[i].loop, holes: [] })
    }
    else {
      // Candidate hole — verify opposite winding relative to parent
      const parent = parentIndex[i]
      if (parent !== null) {
        const parentWinding = signedPolygonArea(loopData[parent].loop)
        const hasOppositeWinding = (winding > 0) !== (parentWinding > 0)

        if (hasOppositeWinding) {
          // Confirmed hole — attach to parent outer contour
          const outerIdx = outerIndexMap.get(parent)
          if (outerIdx !== undefined) {
            outerLoops[outerIdx].holes.push(loopData[i].loop)
          }
        }
        else {
          // Same winding as parent → nested independent solid, treat as outer contour
          outerIndexMap.set(i, outerLoops.length)
          outerLoops.push({ loop: loopData[i].loop, holes: [] })
        }
      }
    }
  }

  // Triangulate each outer contour with its holes using ShapeUtils
  const capTriangles: Vector3[][] = []
  for (const { loop, holes } of outerLoops) {
    if (holes.length === 0) {
      capTriangles.push(...triangulatePolygonEarClipping(loop, faceUp))
    }
    else {
      capTriangles.push(...triangulateContourWithHoles(loop, holes, z, faceUp))
    }
  }

  return capTriangles
}

/**
 * Clip triangles to Z range and generate caps to make the result watertight
 */
function clipTrianglesToZRangeWithCaps(
  triangles: Vector3[][],
  minZ: number,
  maxZ: number,
): Vector3[][] {
  // First do the standard clipping
  const clipped = clipTrianglesToZRange(triangles, minZ, maxZ)

  if (clipped.length === 0) return []

  // Generate bottom cap (at minZ, facing down)
  const bottomCap = generateCapTriangles(clipped, minZ, false)

  // Generate top cap (at maxZ, facing up)
  const topCap = generateCapTriangles(clipped, maxZ, true)

  // Combine all triangles
  return [...clipped, ...bottomCap, ...topCap]
}

/**
 * Extract and clip triangles from a mesh within a specific Z range
 * Returns triangles in WORLD coordinates for further processing
 */
function extractClippedTrianglesInZRange(
  mesh: Mesh,
  minZ: number,
  maxZ: number,
): Vector3[][] {
  mesh.updateMatrixWorld(true)
  const worldMatrix = mesh.matrixWorld

  // Collect all triangles in world coordinates
  const worldTriangles: Vector3[][] = []
  const epsilon = 0.0001

  forEachTriangle(mesh, (i1, i2, i3) => {
    const wv1 = getWorldVertex(mesh, i1, worldMatrix)
    const wv2 = getWorldVertex(mesh, i2, worldMatrix)
    const wv3 = getWorldVertex(mesh, i3, worldMatrix)

    // Quick rejection test
    const minTriZ = Math.min(wv1.z, wv2.z, wv3.z)
    const maxTriZ = Math.max(wv1.z, wv2.z, wv3.z)
    if (maxTriZ < minZ - epsilon || minTriZ > maxZ + epsilon) {
      return
    }

    worldTriangles.push([wv1, wv2, wv3])
  })

  // Clip triangles to the zone and generate caps to make watertight
  return clipTrianglesToZRangeWithCaps(worldTriangles, minZ, maxZ)
}

/**
 * Convert triangles to Float32Array vertices (in local coordinates)
 */
function trianglesToVertices(
  triangles: Vector3[][],
  inverseWorldMatrix: Matrix4,
): Float32Array {
  const vertices: number[] = []
  for (const tri of triangles) {
    for (const worldVertex of tri) {
      const localVertex = worldVertex.clone().applyMatrix4(inverseWorldMatrix)
      vertices.push(localVertex.x, localVertex.y, localVertex.z)
    }
  }
  return new Float32Array(vertices)
}

/**
 * Split a mesh into multiple meshes based on Z-range zones
 * Also separates by XY region when a zone has multiple contours
 */
export function splitMeshByZones(
  originalMesh: Mesh,
  zones: SplitZone[],
): Mesh[] {
  const newMeshes: Mesh[] = []

  originalMesh.updateMatrixWorld(true)
  const inverseWorldMatrix = originalMesh.matrixWorld.clone().invert()

  let globalPartIndex = 0

  for (let zoneIndex = 0; zoneIndex < zones.length; zoneIndex++) {
    const zone = zones[zoneIndex]

    // Get clipped triangles in world coordinates
    const clippedTriangles = extractClippedTrianglesInZRange(originalMesh, zone.minZ, zone.maxZ)

    if (clippedTriangles.length === 0) {
      continue
    }

    // If zone has multiple contours, separate triangles by XY region
    if (zone.contourCount > 1 && zone.contours.length > 1) {
      const trianglesByContour = separateTrianglesByContour(clippedTriangles, zone.contours)

      for (const [, tris] of trianglesByContour) {
        if (tris.length === 0) continue

        const vertices = trianglesToVertices(tris, inverseWorldMatrix)
        if (vertices.length === 0) continue

        globalPartIndex++

        const newGeometry = new BufferGeometry()
        newGeometry.setAttribute('position', new BufferAttribute(vertices, 3))
        newGeometry.computeVertexNormals()

        const newMesh = new Mesh(
          newGeometry,
          (originalMesh.material as MeshStandardMaterial).clone(),
        )

        newMesh.name = `${originalMesh.name}_part${globalPartIndex}`
        newMesh.position.copy(originalMesh.position)
        newMesh.rotation.copy(originalMesh.rotation)
        newMesh.scale.copy(originalMesh.scale)

        newMeshes.push(newMesh)
      }
    }
    else {
      // Single contour - create one mesh for the whole zone
      const vertices = trianglesToVertices(clippedTriangles, inverseWorldMatrix)
      if (vertices.length === 0) continue

      globalPartIndex++

      const newGeometry = new BufferGeometry()
      newGeometry.setAttribute('position', new BufferAttribute(vertices, 3))
      newGeometry.computeVertexNormals()

      const newMesh = new Mesh(
        newGeometry,
        (originalMesh.material as MeshStandardMaterial).clone(),
      )

      newMesh.name = `${originalMesh.name}_part${globalPartIndex}`
      newMesh.position.copy(originalMesh.position)
      newMesh.rotation.copy(originalMesh.rotation)
      newMesh.scale.copy(originalMesh.scale)

      newMeshes.push(newMesh)
    }
  }

  return newMeshes
}

/**
 * Check if an object is a DXF3D based on its userData
 */
function isDxf3D(object: Object3D): boolean {
  return object.userData?.c3dOriginalFileType === 'dxf/3d'
}

/**
 * Check if an object is a DXF2D based on its userData
 */
function isDxf2D(object: Object3D): boolean {
  return object.userData?.c3dOriginalFileType === 'dxf/2d'
}

/**
 * Get all points of a Line, in the line's local space
 */
function getLinePoints(line: Line): Vector3[] {
  const positions = line.geometry.attributes.position.array
  const points: Vector3[] = []
  for (let i = 0; i < positions.length; i += 3) {
    points.push(new Vector3(positions[i], positions[i + 1], positions[i + 2]))
  }
  return points
}

/**
 * Cluster DXF2D lines into physically unlinked parts.
 * Two lines belong to the same part when they share an endpoint (an open/closed
 * contour made of several segments), or when one closed loop is nested inside
 * another (a hole belongs to the shape that contains it). Lines that are merely
 * close together but not touching or nested end up in separate clusters - this
 * undoes the lossy proximity-based merge applied at import time (see
 * groupGroupsBySpatialProximity in dxf-file-loader.ts), which is exactly what
 * "split by contour" should detect as separate, unlinked parts.
 */
function clusterDxf2DLinesByConnectivity(lines: Line[], tolerance = 1e-4): number[][] {
  const n = lines.length
  const parent = Array.from({ length: n }, (_, i) => i)

  const find = (x: number): number => {
    if (parent[x] !== x) parent[x] = find(parent[x])
    return parent[x]
  }
  const union = (a: number, b: number): void => {
    const pa = find(a)
    const pb = find(b)
    if (pa !== pb) parent[pa] = pb
  }

  const points = lines.map(getLinePoints)
  const isClosed = points.map(pts => pts[0].distanceTo(pts.at(-1)!) < tolerance)

  // Union lines that physically touch at an endpoint
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (areLinesConnected(lines[i], lines[j], tolerance)) union(i, j)
    }
  }

  // Union closed loops nested inside another closed loop (holes belong to their outer shape)
  for (let i = 0; i < n; i++) {
    if (!isClosed[i]) continue
    for (let j = 0; j < n; j++) {
      if (i === j || !isClosed[j] || find(i) === find(j)) continue
      if (isPointInPolygon(points[i][0].x, points[i][0].y, points[j])) {
        union(i, j)
      }
    }
  }

  const clusters = new Map<number, number[]>()
  for (let i = 0; i < n; i++) {
    const root = find(i)
    if (!clusters.has(root)) clusters.set(root, [])
    clusters.get(root)!.push(i)
  }

  return Array.from(clusters.values())
}

/**
 * Split a DXF2D object into its physically unlinked parts, based on 2D contour
 * connectivity of the base contour (the child marked IS_ORIGINAL_DXF). This never
 * depends on the "couches" (print layer stacking) setting - the same split happens
 * whether 1 or 100 layers were generated. Each detected part keeps the full layer
 * stack (if any) of the original object.
 */
function splitDxf2DByConnectivity(object: Object3D): Group {
  const resultGroup = new Group()
  resultGroup.name = `${object.name}_split`

  const layerGroups = object.children
  const original = layerGroups.find(child => child.userData?.c3dIsOriginalDxf === true)
  const lines = original?.children.filter((child): child is Line => child instanceof Line) ?? []

  if (!original || lines.length === 0) {
    resultGroup.add(object.clone())
    return resultGroup
  }

  const clusters = clusterDxf2DLinesByConnectivity(lines)

  if (clusters.length <= 1) {
    resultGroup.add(object.clone())
    return resultGroup
  }

  clusters.forEach((indices, clusterIndex) => {
    const indexSet = new Set(indices)

    const partGroup = new Group()
    partGroup.name = `${object.name}_part${clusterIndex + 1}`
    partGroup.userData = { ...object.userData }
    partGroup.visible = object.visible
    partGroup.position.copy(object.position)
    partGroup.rotation.copy(object.rotation)
    partGroup.scale.copy(object.scale)

    for (const layerGroup of layerGroups) {
      const newLayerGroup = new Group()
      newLayerGroup.name = layerGroup.name
      newLayerGroup.userData = { ...layerGroup.userData }
      newLayerGroup.position.copy(layerGroup.position)
      newLayerGroup.rotation.copy(layerGroup.rotation)
      newLayerGroup.scale.copy(layerGroup.scale)

      layerGroup.children.forEach((child, index) => {
        if (indexSet.has(index)) newLayerGroup.add(child.clone())
      })

      partGroup.add(newLayerGroup)
    }

    resultGroup.add(partGroup)
  })

  return resultGroup
}

/**
 * Calculate the signature of a line for shape comparison
 * Returns bounding box and point count
 */
function getLineSignature(line: Line, worldMatrix: Matrix4): {
  minX: number
  maxX: number
  minY: number
  maxY: number
  pointCount: number
  width: number
  height: number
  perimeter: number
} {
  const positions = line.geometry.attributes.position.array
  let minX = Infinity, maxX = -Infinity
  let minY = Infinity, maxY = -Infinity

  const pointCount = positions.length / 3
  const worldPoints: Vector3[] = []

  for (let i = 0; i < positions.length; i += 3) {
    const point = new Vector3(positions[i], positions[i + 1], positions[i + 2])
    point.applyMatrix4(worldMatrix)
    worldPoints.push(point)

    minX = Math.min(minX, point.x)
    maxX = Math.max(maxX, point.x)
    minY = Math.min(minY, point.y)
    maxY = Math.max(maxY, point.y)
  }

  // Calculate perimeter (total line length in XY plane)
  let perimeter = 0
  for (let i = 0; i < worldPoints.length - 1; i++) {
    const dx = worldPoints[i + 1].x - worldPoints[i].x
    const dy = worldPoints[i + 1].y - worldPoints[i].y
    perimeter += Math.sqrt(dx * dx + dy * dy)
  }

  return {
    minX,
    maxX,
    minY,
    maxY,
    pointCount,
    width: maxX - minX,
    height: maxY - minY,
    perimeter,
  }
}

/**
 * Compare two layer signatures and determine if they are significantly different
 * Returns true if the shapes are too different (should split)
 */
function areLayerShapesDifferent(
  sig1: ReturnType<typeof getLineSignature>[],
  sig2: ReturnType<typeof getLineSignature>[],
  tolerance: number = 0.15, // 15% change threshold
): boolean {
  // If line count differs, shapes are different
  if (sig1.length !== sig2.length) {
    return true
  }

  // Calculate total perimeter for each layer
  const totalPerimeter1 = sig1.reduce((sum, s) => sum + s.perimeter, 0)
  const totalPerimeter2 = sig2.reduce((sum, s) => sum + s.perimeter, 0)

  // Check for significant perimeter change (key for detecting inner vs outer cylinder)
  const perimeterChange = Math.abs(totalPerimeter1 - totalPerimeter2) / Math.max(totalPerimeter1, totalPerimeter2, 0.001)
  if (perimeterChange > tolerance) {
    return true
  }

  // Compare total bounding box of all lines in the layer
  const getBoundingBox = (sigs: ReturnType<typeof getLineSignature>[]) => {
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity

    for (const sig of sigs) {
      minX = Math.min(minX, sig.minX)
      maxX = Math.max(maxX, sig.maxX)
      minY = Math.min(minY, sig.minY)
      maxY = Math.max(maxY, sig.maxY)
    }

    return {
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    }
  }

  const bbox1 = getBoundingBox(sig1)
  const bbox2 = getBoundingBox(sig2)

  // Check for significant size change
  const widthChange = Math.abs(bbox1.width - bbox2.width) / Math.max(bbox1.width, bbox2.width, 0.001)
  const heightChange = Math.abs(bbox1.height - bbox2.height) / Math.max(bbox1.height, bbox2.height, 0.001)

  // Check for significant center shift (relative to size)
  const avgWidth = (bbox1.width + bbox2.width) / 2
  const avgHeight = (bbox1.height + bbox2.height) / 2
  const centerShiftX = Math.abs(bbox1.centerX - bbox2.centerX) / Math.max(avgWidth, 0.001)
  const centerShiftY = Math.abs(bbox1.centerY - bbox2.centerY) / Math.max(avgHeight, 0.001)

  const isSizeDifferent = widthChange > tolerance || heightChange > tolerance
  const isCenterShifted = centerShiftX > tolerance || centerShiftY > tolerance

  return isSizeDifferent || isCenterShifted
}

/**
 * Split a DXF3D object by layer changes (where line count or shape changes)
 * Returns a group containing the split line groups
 */
function splitDxf3DByLayerChanges(object: Object3D): Group {
  const resultGroup = new Group()
  resultGroup.name = `${object.name}_split`

  // Copy userData from original object to preserve all properties
  resultGroup.userData = { ...object.userData }

  // Copy other important properties
  resultGroup.visible = object.visible

  // Collect all lines
  const lines: Line[] = []
  object.traverse((child) => {
    if (child instanceof Line) {
      lines.push(child)
    }
  })

  if (lines.length === 0) {
    console.warn('splitDxf3DByLayerChanges: No lines found in object')
    // Return a clone of the original object's children
    object.children.forEach((child) => {
      resultGroup.add(child.clone())
    })
    resultGroup.position.copy(object.position)
    resultGroup.rotation.copy(object.rotation)
    resultGroup.scale.copy(object.scale)
    return resultGroup
  }

  // Update world matrices
  object.updateMatrixWorld(true)

  // Analyze Z levels and collect line signatures
  const zTolerance = 0.0001
  const linesByZ = new Map<number, Line[]>()
  const signaturesByZ = new Map<number, ReturnType<typeof getLineSignature>[]>()

  for (const line of lines) {
    const positions = line.geometry.attributes.position.array
    const firstPoint = new Vector3(positions[0], positions[1], positions[2])
    firstPoint.applyMatrix4(line.matrixWorld)
    const roundedZ = Math.round(firstPoint.z / zTolerance) * zTolerance

    const currentLines = linesByZ.get(roundedZ) || []
    currentLines.push(line)
    linesByZ.set(roundedZ, currentLines)

    // Calculate signature for this line
    const signature = getLineSignature(line, line.matrixWorld)
    const currentSignatures = signaturesByZ.get(roundedZ) || []
    currentSignatures.push(signature)
    signaturesByZ.set(roundedZ, currentSignatures)
  }

  // Get sorted Z levels
  const sortedZLevels = Array.from(linesByZ.keys()).sort((a, b) => a - b)

  if (sortedZLevels.length === 0) {
    resultGroup.position.copy(object.position)
    resultGroup.rotation.copy(object.rotation)
    resultGroup.scale.copy(object.scale)
    return resultGroup
  }

  // Find split points (where line count OR shape changes significantly)
  const splitPoints: number[] = []
  for (let i = 1; i < sortedZLevels.length; i++) {
    const prevZ = sortedZLevels[i - 1]
    const currZ = sortedZLevels[i]
    const prevSignatures = signaturesByZ.get(prevZ) || []
    const currSignatures = signaturesByZ.get(currZ) || []

    // Check if line count changed OR shape changed significantly
    if (areLayerShapesDifferent(prevSignatures, currSignatures)) {
      // Split between prevZ and currZ
      splitPoints.push((prevZ + currZ) / 2)
    }
  }

  // If no split points, wrap all lines in a single child group
  // This ensures totalParts = 1 in the calling code, signaling "no split needed"
  if (splitPoints.length === 0) {
    const singleGroup = new Group()
    singleGroup.name = object.name
    singleGroup.userData = { ...object.userData }
    singleGroup.visible = object.visible

    // Copy position/rotation/scale so the group maintains position when extracted
    singleGroup.position.copy(object.position)
    singleGroup.rotation.copy(object.rotation)
    singleGroup.scale.copy(object.scale)

    for (const line of lines) {
      singleGroup.add(line.clone())
    }

    resultGroup.add(singleGroup)
    // Don't set position on resultGroup - it's on the child group now
    return resultGroup
  }

  // Create zones based on split points
  const minZ = sortedZLevels[0]
  const maxZ = sortedZLevels[sortedZLevels.length - 1]
  const boundaries = [minZ - zTolerance, ...splitPoints, maxZ + zTolerance]

  // Create a group for each zone
  for (let i = 0; i < boundaries.length - 1; i++) {
    const zoneMinZ = boundaries[i]
    const zoneMaxZ = boundaries[i + 1]
    const zoneGroup = new Group()
    zoneGroup.name = `${object.name}_part${i + 1}`

    // Copy userData to each zone group as well
    zoneGroup.userData = { ...object.userData }
    zoneGroup.visible = object.visible

    // Copy position/rotation/scale to each zone group so they maintain position
    // when extracted from resultGroup and added to parent
    zoneGroup.position.copy(object.position)
    zoneGroup.rotation.copy(object.rotation)
    zoneGroup.scale.copy(object.scale)

    // Add lines that fall within this zone
    for (const [z, zLines] of linesByZ.entries()) {
      if (z >= zoneMinZ && z < zoneMaxZ) {
        for (const line of zLines) {
          zoneGroup.add(line.clone())
        }
      }
    }

    if (zoneGroup.children.length > 0) {
      resultGroup.add(zoneGroup)
    }
  }

  // Don't set position on resultGroup - each zone group has its own position
  return resultGroup
}

/**
 * Main function to split an object by contour analysis
 * Returns a group containing the split meshes (for Mesh objects), split line groups
 * (for DXF3D), or split unlinked parts (for DXF2D)
 */
export function splitObjectByContours(
  object: Object3D,
  sliceHeight: number = SLICE_HEIGHT_M,
): Group {
  // Handle DXF3D objects differently - they contain Lines, not Meshes
  if (isDxf3D(object)) {
    return splitDxf3DByLayerChanges(object)
  }

  // Handle DXF2D objects differently - split by 2D contour connectivity, not
  // Z-slicing, and independently of the "couches" (print layer stacking) setting
  if (isDxf2D(object)) {
    return splitDxf2DByConnectivity(object)
  }

  const resultGroup = new Group()
  resultGroup.name = `${object.name}_split`

  // Determine split zones
  const zones = determineSplitZones(object, sliceHeight)

  // Process each mesh in the object
  object.traverse((child) => {
    if (child instanceof Mesh) {
      const splitMeshes = splitMeshByZones(child, zones)

      for (const mesh of splitMeshes) {
        resultGroup.add(mesh)
      }
    }
  })

  // Copy position from original object
  resultGroup.position.copy(object.position)

  return resultGroup
}
